// Shared constants + helpers, keeping the common.tsx pattern from tsf-pittouch.

export const STORAGE_KEYS = {
  // User info captured at login = the app's auth signal (no access token is used).
  userInfo: "user-info",
  orgId: "org-id",
  // Salesforce transaction id (?txn_id=...), used by /v1/auth/verify-mfa.
  txnId: "txn-id",
  // Face-registration status, persisted so the registration gate survives a reload.
  faceStatus: "face-status",
  // Secret-question registration status, persisted so the enrollment gate survives a reload.
  secretStatus: "secret-status",
} as const;

export const ROUTES = {
  login: "/login",
  changePassword: "/change-password",
  faceRegistration: "/face-registration",
  secretQuestions: "/secret-questions",
  secretQuestionsVerify: "/secret-questions/verify",
  checkin: "/verify-mfa",
} as const;

export interface UserInfo {
  sub: string;
  username?: string;
  orgId?: string;
  employeeId?: string;
  [key: string]: unknown;
  user_id?: string;
}

// The signed-in user, saved at login. Its presence is what gates the app (RequireAuth).
export function getUserInfo(): UserInfo | null {
  const raw = localStorage.getItem(STORAGE_KEYS.userInfo);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserInfo;
  } catch {
    return null;
  }
}

export function setUserInfo(user: UserInfo): void {
  localStorage.setItem(STORAGE_KEYS.userInfo, JSON.stringify(user));
}

// Clear the signed-in user (ends the session) but keep org_id — used after checkin,
// where the flow returns to Salesforce and org_id must survive for the next entry.
export function clearUserInfo(): void {
  localStorage.removeItem(STORAGE_KEYS.userInfo);
}

// Salesforce provides org_id / txn_id via the query string (?org_id=...&txn_id=...) on the
// initial redirect. React Router navigation drops the query, so each must be captured once
// and persisted — every later read falls back to the stored value so it is never lost
// mid-flow (login -> change password -> face -> checkin).
function captureParam(queryKey: string, storageKey: string): string {
  const fromQuery = new URLSearchParams(window.location.search).get(queryKey);
  if (fromQuery) {
    localStorage.setItem(storageKey, fromQuery);
    return fromQuery;
  }
  return localStorage.getItem(storageKey) ?? "";
}

export function captureOrgId(): string {
  return captureParam("org_id", STORAGE_KEYS.orgId);
}

export function getOrgId(): string {
  return captureOrgId();
}

export function captureTxnId(): string {
  return captureParam("txn_id", STORAGE_KEYS.txnId);
}

export function getTxnId(): string {
  return captureTxnId();
}

// Full sign-out: drop the user + the Salesforce params (login/401). Checkin uses
// clearUserInfo instead so org_id/txn_id survive for the return to Salesforce.
export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEYS.userInfo);
  localStorage.removeItem(STORAGE_KEYS.orgId);
  localStorage.removeItem(STORAGE_KEYS.txnId);
  localStorage.removeItem(STORAGE_KEYS.faceStatus);
  localStorage.removeItem(STORAGE_KEYS.secretStatus);
}

export function redirectUrlLogin(): void {
  if (window.location.pathname !== ROUTES.login) {
    window.location.href = ROUTES.login;
  }
}
